import React, { FC, ReactNode, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

interface DropdownListContainerProps {
    $orientation?: 'LEFT' | 'RIGHT';
    $verticalOrientation?: 'TOP' | 'BOTTOM';
}

const DropDownContainer = styled.div`
    width: auto;
    display: flex;
    position: relative;
    align-items: center;
`;

const DropDownHeader = styled.div`
    user-select: none;
    align-self: center;
    display: flex;
`;

export const DropDownListContainer = styled.div<DropdownListContainerProps>`
    z-index: 50;
    position: absolute;
    width: auto;
    ${(props) => (!props.$orientation || props.$orientation === 'LEFT') && `left: 0;`}
    ${(props) => props.$orientation && props.$orientation === 'RIGHT' && `right: 0;`}
	${(props) => (!props.$verticalOrientation || props.$verticalOrientation === 'TOP') && `top: calc(100% + 6px);`}
	${(props) => props.$verticalOrientation && props.$verticalOrientation === 'BOTTOM' && `bottom: calc(100% + 6px);`}
	min-width: 10rem;
`;

export const DropDownList = styled.div`
    width: min-content !important;
    padding: 4px 0;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    min-width: 300px;

    & > * {
        user-select: none;
    }
`;

const ListItem = styled.div`
    list-style: none;
    width: 100%;
    display: flex;
    flex-direction: row;
    white-space: nowrap;
`;

const Divider = styled.div`
    height: 1px;
    background: #e5e7eb;
    margin: 4px 0;
`;

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 49;
`;

interface Props {
    header: string | ReactNode;
    /** Array of sections, each section is an array of items. A divider is rendered between sections. */
    sections?: ReactNode[][];
    /** Flat list of items (legacy, no dividers). Use `sections` for grouped items. */
    items?: ReactNode[];
    orientation?: 'LEFT' | 'RIGHT';
    verticalOrientation?: 'TOP' | 'BOTTOM';
}

export const SelectActions: FC<Props> = ({ header, items, sections, orientation, verticalOrientation }) => {
    const wrapperRef = useRef(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [computedVertical, setComputedVertical] = useState<'TOP' | 'BOTTOM'>(verticalOrientation ?? 'TOP');

    const toggling = useCallback(() => {
        if (!isOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            setComputedVertical(spaceBelow < 250 ? 'BOTTOM' : (verticalOrientation ?? 'TOP'));
        }
        setIsOpen(!isOpen);
    }, [isOpen, verticalOrientation]);

    const renderItems = (itemList: ReactNode[], keyPrefix: string) => itemList.map((item, i) => item && <ListItem key={`${keyPrefix}_${i}`}>{item}</ListItem>);

    return (
        <DropDownContainer
            ref={containerRef}
            onClick={(e) => {
                e.stopPropagation();
                toggling();
            }}
        >
            <DropDownHeader>{header}</DropDownHeader>
            {isOpen && (
                <>
                    <Backdrop onMouseDown={() => setIsOpen(false)} />
                    <DropDownListContainer $orientation={orientation} $verticalOrientation={computedVertical} ref={wrapperRef}>
                        <DropDownList>
                            {sections
                                ? sections.map((section, sIdx) => {
                                      const sectionItems = section.filter(Boolean);
                                      if (sectionItems.length === 0) return null;
                                      return (
                                          <React.Fragment key={`section_${sIdx}`}>
                                              {sIdx > 0 && <Divider />}
                                              {renderItems(sectionItems, `s${sIdx}`)}
                                          </React.Fragment>
                                      );
                                  })
                                : items && renderItems(items, 'item')}
                        </DropDownList>
                    </DropDownListContainer>
                </>
            )}
        </DropDownContainer>
    );
};
